"use client";

import Link from "next/link";
import { useEffect } from "react";

const MERCHANT_DYNAMIC_SECTIONS = new Set([
    "payments",
    "transactions",
    "settings",
    "conversion",
    "withdrawals",
    "team",
]);

function isDynamicShellRoute(pathname: string): boolean {
    const parts = pathname.split("/").filter(Boolean);
    if (parts[0] !== "overview") return false;

    if (parts[1] === "tickets") {
        return parts.length === 3;
    }

    if (parts[1] !== "merchants") return false;

    if (parts[2] === "create") {
        return parts.length === 4 || (parts.length === 5 && parts[4] === "kyb");
    }

    return (
        parts.length === 3 ||
        (parts.length === 4 && MERCHANT_DYNAMIC_SECTIONS.has(parts[3]))
    );
}

function shouldReloadOnce(url: string): boolean {
    try {
        const key = `_not_found_reload:${url}`;
        if (sessionStorage.getItem(key)) {
            sessionStorage.removeItem(key);
            return false;
        }
        sessionStorage.setItem(key, "1");
        return true;
    } catch {
        return false;
    }
}

export default function NotFound() {
    useEffect(() => {
        (window as unknown as { hidePreloader?: () => void }).hidePreloader?.();

        if (!isDynamicShellRoute(window.location.pathname)) return;

        const url = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        if (shouldReloadOnce(url)) {
            window.location.replace(url);
        }
    }, []);

    return (
        <main className="min-h-screen bg-[#13141B] text-white flex items-center justify-center px-6 py-16">
            <section className="max-w-[560px] text-center">
                <p className="text-sm font-medium uppercase text-[#0079ec]">
                    404
                </p>
                <h1 className="mt-5 text-4xl md:text-5xl font-semibold leading-tight">
                    Page not found
                </h1>
                <p className="mt-5 text-base md:text-lg leading-7 text-white/60">
                    The page may have been moved, removed, or the address may be incorrect.
                </p>
                <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
                    <Link
                        href="/"
                        className="inline-flex h-12 w-full sm:w-auto items-center justify-center rounded-lg bg-[#0079ec] px-6 text-sm font-semibold text-white transition hover:bg-[#006ed8]"
                    >
                        Go home
                    </Link>
                    <Link
                        href="/overview"
                        className="inline-flex h-12 w-full sm:w-auto items-center justify-center rounded-lg border border-white/15 px-6 text-sm font-semibold text-white/85 transition hover:border-white/30 hover:bg-white/5"
                    >
                        Open dashboard
                    </Link>
                </div>
            </section>
        </main>
    );
}
